import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@astro/auth'
import { api } from '@astro/api'
import { appUrl } from '@astro/config'
import { DateField } from '@astro/date-field'
import { useToast } from '../context/ToastContext.jsx'
import { formatRupees, pujaDateInfo, scheduleLabel, apiErrorMessage } from '../services/pujaFormat.js'
import { loadBookingDraft, clearBookingDraft } from '../services/poojaBookingDraft.js'
import { payForPujaBooking } from '../checkout/pujaRazorpay.js'
import PaymentProcessingOverlay from '../checkout/PaymentProcessingOverlay.jsx'
import { PoojaImageFallback } from '../components/pooja/PoojaCard.jsx'

const GENDERS = ['male', 'female', 'other']

const todayYmd = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Session/API mobiles may carry a country code — the form wants bare 10 digits. */
const normalizeMobile = (raw = '') => {
  const digits = String(raw).replace(/\D/g, '')
  return digits.length > 10 ? digits.slice(-10) : digits
}

/**
 * Sankalp checkout — layout mirrors the product checkout page (accordion
 * sections, trust cards, gateway sidebar, authorize button) while every call
 * comes from the puja API set (PUJA_API_EXAMPLES.md):
 *
 *   my-bookings → booking-details   prefill address + devotee from the last booking
 *   update-address                  save/refresh the puja address book
 *   create-booking                  with { puja_address_id } (or the inline block)
 *   create-order → verify-payment   Razorpay leg
 *
 * If payment fails or is dismissed, the pending booking is kept so Retry
 * skips straight to a fresh Razorpay order instead of duplicating the booking.
 */
export default function PoojaCheckout() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { status, user } = useAuth()

  // Draft written by the details page; localStorage-persisted so it survives
  // the RequireAuth login redirect.
  const [draft] = useState(() => loadBookingDraft())
  const maxDevotees = draft?.package?.max_devotees ?? 1

  const [pujaDate, setPujaDate] = useState(draft?.puja?.puja_date ?? '')
  const [primary, setPrimary] = useState({
    name: '', gotra: 'Kashyap', gender: '', dob: '', mobile: '', email: '',
  })
  const [others, setOthers] = useState(() =>
    Array.from({ length: Math.max(0, maxDevotees - 1) }, () => ({ name: '', gotra: '' })),
  )
  const [address, setAddress] = useState({
    name: '', mobile: '', email: '', address: '', landmark: '',
    city: '', state: '', country: 'India', pincode: '',
  })
  // The puja address book record backing the form (from the last booking's
  // address). Untouched → create-booking sends { puja_address_id }; edited →
  // update-address refreshes the record first (doc §16).
  const [savedAddressId, setSavedAddressId] = useState(null)
  const addressDirtyRef = useRef(false)

  const [placing, setPlacing] = useState(false)
  const [verifying, setVerifying] = useState(false)
  // Booking created but not yet paid — Retry reuses it (no duplicate booking).
  const [pendingBooking, setPendingBooking] = useState(null)

  // Prefill the primary devotee and delivery contact from the signed-in
  // user's session state. Only empty fields are filled, so nothing the
  // devotee already typed gets overwritten.
  useEffect(() => {
    if (!user) return
    const mobile = normalizeMobile(user.mobile)
    setPrimary((p) => ({
      ...p,
      name: p.name || user.name || '',
      email: p.email || user.email || '',
      mobile: p.mobile || mobile,
    }))
    setAddress((a) => ({
      ...a,
      name: a.name || user.name || '',
      email: a.email || user.email || '',
      mobile: a.mobile || mobile,
    }))
  }, [user])

  // The puja API has no standalone address-list read — the saved address (and
  // sankalp identity) ride on the most recent booking: GET /my-bookings for
  // the latest id, GET /booking-details/{id} for its address + devotees.
  const { data: myBookingsRes } = useQuery({
    queryKey: ['pujaMyBookings', 'latest'],
    queryFn: () => api.puja.myBookings({ limit: 1 }),
    enabled: status === 'authenticated',
    retry: false,
  })
  const lastBookingId = myBookingsRes?.data?.[0]?.id ?? null
  const { data: lastBookingRes } = useQuery({
    queryKey: ['pujaBookingDetails', lastBookingId],
    queryFn: () => api.puja.bookingDetails(lastBookingId),
    enabled: lastBookingId != null,
    retry: false,
  })

  useEffect(() => {
    const booking = lastBookingRes?.success ? lastBookingRes.data : null
    if (!booking) return

    const a = booking.address
    if (a) {
      setSavedAddressId(a.id ?? null)
      if (!addressDirtyRef.current) {
        setAddress((prev) => ({
          ...prev,
          name: prev.name || a.name || '',
          mobile: prev.mobile || normalizeMobile(a.mobile),
          email: prev.email || a.email || '',
          address: prev.address || a.address || '',
          landmark: prev.landmark || a.landmark || '',
          city: prev.city || a.city || '',
          state: prev.state || a.state || '',
          country: a.country || prev.country,
          pincode: prev.pincode || a.pincode || '',
        }))
      }
    }

    const d = (booking.devotees ?? []).find((x) => x.is_primary) ?? booking.devotees?.[0]
    if (d) {
      setPrimary((p) => ({
        ...p,
        name: p.name || d.name || '',
        // 'Kashyap' is only the seed value — a real gotra from the last
        // sankalp is the better default.
        gotra: p.gotra === 'Kashyap' && d.gotra ? d.gotra : p.gotra,
        gender: p.gender || d.gender || '',
        dob: p.dob || d.dob || '',
        mobile: p.mobile || normalizeMobile(d.mobile),
        email: p.email || d.email || '',
      }))
    }
  }, [lastBookingRes])

  const dateInfo = useMemo(() => pujaDateInfo(pujaDate), [pujaDate])
  const [imgFailed, setImgFailed] = useState(false)

  if (!draft) {
    return (
      <div className="flex flex-col min-h-screen bg-background font-normal uppercase select-none">
        <div className="container px-6 mx-auto pt-28 md:pt-32 pb-12">
          <div className="bg-white rounded-3xl border border-border/40 shadow-sm py-24 text-center">
            <i className="fa-solid fa-hands-praying text-4xl text-primary/20"></i>
            <p className="mt-5 text-sm text-primary/50 font-body normal-case">No pooja selected yet — choose a ritual to begin your sankalp.</p>
            <button
              type="button"
              onClick={() => navigate('/pooja-energization')}
              className="mt-6 inline-flex items-center justify-center h-11 px-8 rounded-xl bg-secondary text-white hover:bg-primary transition-all uppercase tracking-widest text-[10px]"
            >
              Explore Upcoming Poojas
            </button>
          </div>
        </div>
      </div>
    )
  }

  const pkg = draft.package
  const discount = Math.max(0, (pkg.original_price ?? pkg.final_price) - pkg.final_price)

  const setPrimaryField = (key, value) => setPrimary((p) => ({ ...p, [key]: value }))
  const setOtherField = (i, key, value) =>
    setOthers((prev) => prev.map((o, idx) => (idx === i ? { ...o, [key]: value } : o)))
  const setAddressField = (key, value) => {
    addressDirtyRef.current = true
    setAddress((a) => ({ ...a, [key]: value }))
  }
  const copyPrimaryToAddress = () => {
    addressDirtyRef.current = true
    setAddress((a) => ({ ...a, name: primary.name, mobile: primary.mobile, email: primary.email }))
  }

  const validate = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(pujaDate)) return 'Select the pooja date'
    if (!primary.name.trim()) return 'Enter the primary devotee name'
    if (!primary.gotra.trim()) return 'Enter your gotra — use "Kashyap" if unknown'
    if (!/^\d{10}$/.test(primary.mobile)) return 'Enter a valid 10-digit mobile number for the primary devotee'
    if (primary.email && !/^\S+@\S+\.\S+$/.test(primary.email)) return 'Enter a valid email for the primary devotee'
    if (!address.name.trim()) return 'Enter the delivery contact name'
    if (!/^\d{10}$/.test(address.mobile)) return 'Enter a valid 10-digit delivery mobile number'
    if (!address.address.trim()) return 'Enter the delivery address'
    if (!address.city.trim()) return 'Enter the delivery city'
    if (!address.state.trim()) return 'Enter the delivery state'
    if (!address.country.trim()) return 'Enter the delivery country'
    if (!/^\d{6}$/.test(address.pincode)) return 'Enter a valid 6-digit pincode'
    return null
  }

  const addressBlock = () => ({
    name: address.name.trim(),
    mobile: address.mobile,
    ...(address.email.trim() && { email: address.email.trim() }),
    address: address.address.trim(),
    ...(address.landmark.trim() && { landmark: address.landmark.trim() }),
    city: address.city.trim(),
    state: address.state.trim(),
    country: address.country.trim(),
    pincode: address.pincode,
  })

  /** create-booking's `address`: an untouched saved record goes by reference;
   * otherwise POST /update-address saves/refreshes the puja address book and
   * the new id goes by reference. The inline block is only the fallback when
   * that save call fails. */
  const resolveAddressPayload = async () => {
    if (savedAddressId && !addressDirtyRef.current) {
      return { puja_address_id: savedAddressId }
    }
    try {
      const res = await api.puja.updateAddress({
        ...(savedAddressId && { puja_address_id: savedAddressId }),
        ...addressBlock(),
        is_default: true,
      })
      const id = res?.success ? res?.data?.id : null
      if (id) {
        setSavedAddressId(id)
        addressDirtyRef.current = false
        return { puja_address_id: id }
      }
    } catch {
      /* fall through to the inline block */
    }
    return addressBlock()
  }

  const authorize = async () => {
    const problem = validate()
    if (problem) return showToast(problem)

    setPlacing(true)
    try {
      // Step 1 — create the pending booking (skipped on payment retry).
      let booking = pendingBooking
      if (!booking) {
        const res = await api.puja.createBooking({
          puja_id: draft.puja.id,
          puja_package_id: pkg.id,
          puja_date: pujaDate,
          devotees: [
            {
              is_primary: true,
              name: primary.name.trim(),
              gotra: primary.gotra.trim(),
              ...(primary.gender && { gender: primary.gender }),
              ...(primary.dob && { dob: primary.dob }),
              mobile: primary.mobile,
              ...(primary.email.trim() && { email: primary.email.trim() }),
            },
            ...others
              .filter((o) => o.name.trim())
              .map((o) => ({
                is_primary: false,
                name: o.name.trim(),
                gotra: o.gotra.trim() || primary.gotra.trim(),
              })),
          ],
          address: await resolveAddressPayload(),
        })
        if (!res?.success || !res?.data) {
          return showToast(res?.message || 'Could not create the booking — try again.')
        }
        booking = res.data
        setPendingBooking(booking)
        // The booking response carries the saved address id — remember it so
        // the next booking can go by reference.
        if (booking.address?.id) setSavedAddressId(booking.address.id)
      }

      // Step 2 — Razorpay order for the booking.
      const orderRes = await api.puja.createOrder({ puja_booking_id: booking.id })
      if (!orderRes?.success || !orderRes?.data) {
        return showToast(orderRes?.message || 'Could not start the payment — try again.')
      }

      // Step 3 — popup + server-side verification.
      await payForPujaBooking({
        booking,
        order: orderRes.data,
        primary,
        onVerifyStart: () => setVerifying(true),
        onSuccess: (confirmed) => {
          clearBookingDraft()
          navigate('/pooja-energization/booking-confirmed', {
            state: { booking: confirmed, mobile: primary.mobile },
          })
        },
        onFailure: (message) => {
          setVerifying(false)
          showToast(message)
        },
      })
    } catch (err) {
      showToast(apiErrorMessage(err, 'Could not place the booking — try again.'))
    } finally {
      setPlacing(false)
      setVerifying(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-background font-normal uppercase select-none">
      {verifying && <PaymentProcessingOverlay />}
      <div className="container px-6 mx-auto pt-28 md:pt-32 pb-12">
        {/* Back button */}
        <div className="mb-10">
          <Link
            to={`/pooja-energization/${draft.puja.slug}`}
            className="group flex items-center gap-2 text-primary/40 font-normal uppercase tracking-widest text-[10px] hover:text-primary transition-colors w-fit"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"></path></svg>
            BACK TO POOJA DETAILS
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* LEFT: Item + Sankalp + Delivery + Trust */}
          <div className="lg:col-span-7 space-y-8">

            {pendingBooking && (
              <div className="flex items-center gap-3 px-5 py-3 rounded-2xl border border-secondary/30 bg-secondary/5">
                <i className="fa-solid fa-bolt text-secondary text-xs"></i>
                <p className="text-[9px] font-normal uppercase tracking-widest text-primary/60">
                  Booking #{pendingBooking.booking_number} is reserved — complete the payment to confirm it
                </p>
              </div>
            )}

            {/* Item card (Cart item-list treatment) */}
            <div className="bg-muted/5 rounded-4xl shadow-inner">
              <div className="bg-white rounded-3xl border border-border/10 overflow-hidden shadow-sm">
                <div className="p-4 flex flex-col sm:flex-row gap-6 items-center bg-white">
                  <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-border shrink-0 shadow-sm bg-white">
                    {imgFailed || !draft.puja.image ? (
                      <PoojaImageFallback />
                    ) : (
                      <img
                        src={draft.puja.image}
                        alt={draft.puja.title}
                        onError={() => setImgFailed(true)}
                        className="object-cover w-full h-full"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1 text-center sm:text-left">
                    <p className="text-[9px] font-normal text-secondary uppercase tracking-widest">
                      {scheduleLabel(draft.puja)}{draft.puja.category?.title ? ` · ${draft.puja.category.title}` : ''}
                    </p>
                    <h4 className="text-lg font-heading text-primary line-clamp-2 uppercase leading-snug">{draft.puja.title}</h4>
                    {draft.puja.temple?.name && (
                      <p className="text-[9px] normal-case tracking-wider text-primary/50">
                        <span className="uppercase text-primary/35">Temple:</span>{' '}
                        {[draft.puja.temple.name, draft.puja.temple.location, draft.puja.temple.state].filter(Boolean).join(', ')}
                      </p>
                    )}
                    <p className="text-primary font-normal">{formatRupees(pkg.final_price)}</p>
                    <p className="text-[9px] normal-case tracking-wider text-primary/50">
                      <span className="uppercase text-primary/35">Package:</span> {pkg.title}
                      {pkg.subtitle ? ` — ${pkg.subtitle}` : ''} · up to {pkg.max_devotees} devotee{pkg.max_devotees > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Pooja date */}
                <div className="border-t border-border px-6 py-4">
                  {draft.puja.puja_date ? (
                    <p className="text-[10px] text-primary/60 normal-case">
                      <i className="fa-solid fa-calendar-check text-secondary mr-2"></i>
                      Pooja date: <span className="font-semibold text-primary">{dateInfo.weekday}, {dateInfo.label}</span>
                    </p>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <label htmlFor="puja-date" className="text-[10px] font-semibold text-primary/50 uppercase tracking-widest shrink-0">
                        CHOOSE POOJA DATE *
                      </label>
                      <DateField
                        id="puja-date"
                        min={todayYmd()}
                        value={pujaDate}
                        onChange={(e) => setPujaDate(e.target.value)}
                        className="w-full sm:max-w-56 h-12 px-4 rounded-xl border border-border bg-white text-sm text-primary font-normal normal-case focus:outline-none focus:border-secondary transition-colors"
                      />
                      {dateInfo.label && (
                        <span className="text-[10px] text-secondary font-bold normal-case">{dateInfo.weekday}, {dateInfo.label}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sankalp details — accordion (AddressBook treatment) */}
            <Accordion
              defaultOpen
              title="SANKALP DETAILS"
              subtitle={`${maxDevotees} DEVOTEE IDENTITY POINT${maxDevotees > 1 ? 'S' : ''}`}
              icon={
                <svg className="w-6 h-6 text-secondary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" /><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
                </svg>
              }
            >
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="PRIMARY DEVOTEE NAME *" placeholder="Full name" value={primary.name} onChange={(e) => setPrimaryField('name', e.target.value)} />
                  <Field label="GOTRA *" placeholder="Gotra" value={primary.gotra} onChange={(e) => setPrimaryField('gotra', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label="MOBILE NUMBER *" placeholder="10-digit mobile number" inputMode="numeric" maxLength={10}
                    value={primary.mobile} onChange={(e) => setPrimaryField('mobile', e.target.value.replace(/\D/g, ''))}
                  />
                  <Field label="EMAIL (OPTIONAL)" type="email" placeholder="Email address" value={primary.email} onChange={(e) => setPrimaryField('email', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <div>
                    <p className="text-[10px] font-semibold text-primary/50 uppercase tracking-widest mb-2">GENDER (OPTIONAL)</p>
                    <div className="flex items-center gap-6 h-12">
                      {GENDERS.map((g) => (
                        <label key={g} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="primary-gender"
                            value={g}
                            checked={primary.gender === g}
                            onChange={() => setPrimaryField('gender', g)}
                            className="accent-[#E8770F] h-4 w-4"
                          />
                          <span className="text-xs text-primary/70 uppercase tracking-wide">{g}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Field label="DATE OF BIRTH (OPTIONAL)" type="date" max={todayYmd()} value={primary.dob} onChange={(e) => setPrimaryField('dob', e.target.value)} />
                </div>
                <p className="text-[10px] text-primary/40 font-body normal-case leading-relaxed">
                  Don't know your gotra? Keep "Kashyap" — as prescribed by shastra. Booking updates arrive on the mobile number.
                </p>

                {others.length > 0 && (
                  <div className="pt-4 border-t border-dashed border-border/40 space-y-3">
                    <p className="text-[10px] font-semibold text-primary/50 uppercase tracking-widest">
                      ADDITIONAL DEVOTEES (UP TO {others.length} MORE IN {pkg.title})
                    </p>
                    {others.map((o, i) => (
                      <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label={`DEVOTEE ${i + 2} NAME`} placeholder="Full name (optional)" value={o.name} onChange={(e) => setOtherField(i, 'name', e.target.value)} />
                        <Field label="GOTRA" placeholder="Defaults to primary's gotra" value={o.gotra} onChange={(e) => setOtherField(i, 'gotra', e.target.value)} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Accordion>

            {/* Delivery coordinates — puja address book (POST /update-address;
                prefilled from the last booking's saved record) */}
            <Accordion
              defaultOpen
              title="DELIVERY COORDINATES"
              subtitle={savedAddressId ? 'SAVED ADDRESS ON FILE' : 'PRASAD DISPATCH POINT'}
              icon={
                <svg className="w-6 h-6 text-secondary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              }
            >
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={copyPrimaryToAddress}
                    className="text-[10px] font-semibold uppercase tracking-widest text-secondary hover:text-primary transition-colors"
                  >
                    <i className="fa-solid fa-copy mr-1.5"></i>SAME AS PRIMARY DEVOTEE
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="CONTACT NAME *" placeholder="Full name" value={address.name} onChange={(e) => setAddressField('name', e.target.value)} />
                  <Field
                    label="MOBILE NUMBER *" placeholder="10-digit mobile number" inputMode="numeric" maxLength={10}
                    value={address.mobile} onChange={(e) => setAddressField('mobile', e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <Field label="ADDRESS (HOUSE NO, BUILDING, AREA) *" placeholder="Flat / House no., Building, Street, Area" value={address.address} onChange={(e) => setAddressField('address', e.target.value)} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="CITY / DISTRICT *" placeholder="City" value={address.city} onChange={(e) => setAddressField('city', e.target.value)} />
                  <Field label="STATE *" placeholder="State" value={address.state} onChange={(e) => setAddressField('state', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label="PINCODE *" placeholder="6-digit pincode" inputMode="numeric" maxLength={6}
                    value={address.pincode} onChange={(e) => setAddressField('pincode', e.target.value.replace(/\D/g, ''))}
                  />
                  <Field label="COUNTRY *" placeholder="Country" value={address.country} onChange={(e) => setAddressField('country', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="LANDMARK (OPTIONAL)" placeholder="Nearby landmark" value={address.landmark} onChange={(e) => setAddressField('landmark', e.target.value)} />
                  <Field label="EMAIL (OPTIONAL)" type="email" placeholder="Email address" value={address.email} onChange={(e) => setAddressField('email', e.target.value)} />
                </div>
                {savedAddressId && (
                  <p className="text-[10px] text-primary/40 font-body normal-case leading-relaxed">
                    Prefilled from your last booking — edits update your saved puja address.
                  </p>
                )}
              </div>
            </Accordion>

            {/* Trust cards (Cart treatment) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TrustCard title="SYSTEM SECURITY" body="your transaction point is processed via an authenticated mission gateway with clinical encryption.">
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" />
              </TrustCard>
              <TrustCard title="DATA PROTECTION" body="devotee names, gotra and sankalp details remain private and are shared only with the temple priest.">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </TrustCard>
            </div>
          </div>

          {/* RIGHT: Payment sidebar (Cart treatment) */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="bg-white rounded-3xl border border-border/40 p-8 md:p-10 space-y-10 shadow-2xl shadow-primary/5">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-secondary animate-pulse"></div>
                <span className="text-[9px] font-normal text-secondary uppercase tracking-widest">ORDER SUMMARY</span>
              </div>

              {/* Pricing */}
              <div className="space-y-5">
                <div className="flex justify-between items-center text-sm font-body">
                  <span className="text-primary/40 uppercase tracking-widest text-[10px] font-normal">{pkg.title} PACKAGE</span>
                  <span className="text-primary font-normal">{formatRupees(pkg.original_price ?? pkg.final_price)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between items-center text-sm font-body">
                    <span className="text-primary/40 uppercase tracking-widest text-[10px] font-normal">
                      DISCOUNT{pkg.offer_percentage != null ? ` (${pkg.offer_percentage}%)` : ''}
                    </span>
                    <span className="text-secondary font-normal">−{formatRupees(discount)}</span>
                  </div>
                )}
                {dateInfo.label && (
                  <div className="flex justify-between items-center text-sm font-body">
                    <span className="text-primary/40 uppercase tracking-widest text-[10px] font-normal">POOJA DATE</span>
                    <span className="text-primary/70 font-normal text-xs normal-case">{dateInfo.weekday}, {dateInfo.label}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-6 border-t border-border mt-2">
                  <div>
                    <span className="font-heading text-primary text-lg block font-normal uppercase">TOTAL PAYABLE</span>
                    <span className="text-[9px] font-mono font-normal text-primary/20 uppercase tracking-[0.2em]">INCL. OF ALL TAXES</span>
                  </div>
                  <span className="text-4xl font-heading text-primary font-normal">{formatRupees(pkg.final_price)}</span>
                </div>
              </div>

              {/* Payment gateways (Cart treatment) */}
              <div className="pt-6 border-t border-dashed border-border/40 space-y-8">
                <div className="flex items-center gap-4 px-1">
                  <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-secondary border border-primary/10">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-[10px] font-mono font-normal text-primary/40 uppercase tracking-[0.3em]">Secure.Transaction.Protocol</p>
                    <h4 className="text-sm font-normal text-primary uppercase tracking-widest">Verified Payment Gateway</h4>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Gateway title="UPI system" sub="GPay, PhonePe, Paytm">
                    <rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" />
                  </Gateway>
                  <Gateway title="Card Synchronization" sub="Visa, Mastercard, RuPay">
                    <rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" />
                  </Gateway>
                  <Gateway title="Institutional Banking" sub="All Major Indian Banks">
                    <line x1="3" x2="21" y1="22" y2="22" /><line x1="6" x2="6" y1="18" y2="11" /><line x1="10" x2="10" y1="18" y2="11" /><line x1="14" x2="14" y1="18" y2="11" /><line x1="18" x2="18" y1="18" y2="11" /><polygon points="12 2 20 7 4 7" />
                  </Gateway>
                </div>

                <div className="space-y-4 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border/40"></div>
                    <span className="text-[8px] font-mono font-normal text-primary/20 uppercase tracking-[0.4em]">Supported.Points</span>
                    <div className="h-px flex-1 bg-border/40"></div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2">
                    {['Visa', 'Mastercard', 'Maestro', 'RuPay', 'UPI', 'Netbanking'].map((t) => (
                      <span key={t} className="px-3 py-1.5 rounded-lg border border-border/40 bg-muted/30 text-[9px] font-normal text-primary/40 uppercase tracking-widest">{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Authorize */}
              <button
                onClick={authorize}
                disabled={placing}
                className="inline-flex items-center justify-center whitespace-nowrap active:scale-95 w-full h-16 rounded-2xl bg-secondary text-white hover:bg-primary transition-all duration-500 font-normal uppercase tracking-[0.3em] text-xs shadow-2xl shadow-secondary/20 group disabled:opacity-60 disabled:pointer-events-none"
              >
                <div className="flex items-center gap-3">
                  {placing ? (
                    <>PROCESSING PAYMENT<i className="fa-solid fa-spinner animate-spin"></i></>
                  ) : (
                    <>{pendingBooking ? 'RETRY PAYMENT' : 'AUTHORIZE TRANSACTION'}
                      <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                    </>
                  )}
                </div>
              </button>

              {/* Linked terms before payment. The sankalp cut-off is the one
                  thing a devotee must know before paying, so it is named here
                  and not just buried in the policy. */}
              <p className="mt-3 text-[11px] leading-relaxed text-primary/45 font-body text-center">
                By authorizing this payment you agree to our{' '}
                <a href={appUrl('website', 'terms-of-service')} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Terms of Service</a>{' '}
                and{' '}
                <a href={appUrl('website', 'refund-policy')} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Cancellation &amp; Refund Policy</a>
                . A seva can be cancelled for a full refund until the sankalp is taken in your name.
              </p>
            </div>

            <div className="flex items-center gap-3 px-4 py-3 mt-4 bg-muted/20 rounded-xl border border-border/40">
              <svg className="w-4 h-4 text-primary/20 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
              <p className="text-[9px] text-primary/40 font-body leading-tight uppercase tracking-wider font-normal">
                YOUR BOOKING IS CONFIRMED ONLY AFTER PAYMENT VERIFICATION. THE INVOICE AND POOJA VIDEO ARRIVE ON YOUR REGISTERED CONTACT POINTS.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

/** Accordion shell matching the product checkout's AddressBook/PaymentOptions. */
function Accordion({ title, subtitle, icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bg-white rounded-3xl border border-border/40 overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full p-6 flex items-center gap-4 bg-muted/10 hover:bg-muted/20 transition-colors text-left"
      >
        {icon}
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-heading text-primary font-normal uppercase">{title}</h3>
          <p className="text-[10px] font-mono font-normal text-primary/30 uppercase tracking-widest">{subtitle}</p>
        </div>
        <svg
          className={`w-5 h-5 text-primary/40 shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && <div className="border-t border-border p-6 md:p-8">{children}</div>}
    </div>
  )
}

/** Labelled input matching the product checkout's address-modal fields.
 *  `type="date"` renders the shared picker instead of the native control. */
function Field({ label, className = '', type, ...props }) {
  const cls = `w-full h-12 px-4 rounded-xl border border-border bg-white text-sm text-primary font-normal normal-case placeholder:text-primary/30 focus:outline-none focus:border-secondary transition-colors ${className}`
  return (
    <div>
      <label className="block text-[10px] font-semibold text-primary/50 uppercase tracking-widest mb-2">{label}</label>
      {type === 'date'
        ? <DateField className={cls} {...props} />
        : <input type={type} className={cls} {...props} />}
    </div>
  )
}

function TrustCard({ title, body, children }) {
  return (
    <div className="group p-8 bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-lg hover:border-secondary/20 transition-all duration-300 space-y-4">
      <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary">{children}</svg>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-primary uppercase tracking-widest">{title}</h4>
        <p className="text-xs text-primary/60 mt-2 leading-relaxed lowercase">{body}</p>
      </div>
    </div>
  )
}

function Gateway({ title, sub, children }) {
  return (
    <div className="p-5 rounded-2xl border border-border/60 bg-white shadow-sm flex flex-col gap-4 group hover:border-secondary transition-all duration-500">
      <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center text-primary/40 group-hover:bg-primary group-hover:text-white transition-all">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
      </div>
      <div className="space-y-1">
        <p className="text-[10px] font-normal text-primary uppercase tracking-widest">{title}</p>
        <p className="text-[9px] text-primary/40 font-body uppercase tracking-tight font-normal">{sub}</p>
      </div>
    </div>
  )
}
